import { useEffect, useRef, useState } from "react";
import styles from "../../components/live-support/liveSupportComponent.module.scss";
import MessageIcon from "../../assets/images/pages/home/hero-section/messageIcon.png";
import SupportServices from "./services/SupportService";
import LiveChatForm from "./LiveChatForm";
import LiveChatSection from "./LiveChatSection";
import { getCookie, setCookie } from "../../api/utils/utils";
import { RootState } from "../../redux/store";
import { useSelector } from "react-redux";
import WhatsAppButton from "./WhatsappService";
import { useAppSelector } from "../../hooks/hook";

interface Template {
  id: number;
  title: string;
  is_parent: boolean;
  is_file: number;
  title_link?: string | null;
  link_title?: string | null;
  children?: Template[];
  content?: string;
}

interface Message {
  id: number;
  message: string;
  isUser: boolean;
  timestamp: string;
  isParent?: boolean;
  templateId?: number;
  hasChildren?: boolean;
  isContent?: boolean;
  isLink?: boolean;
  is_file: number;
}

const LiveSupportComponent = () => {
  const [isLiveSupport, setIsLiveSupport] = useState(false);
  const [chatBotTemplates, setChatBotTemplates] = useState<Template[]>([]);
  const [childTemplates, setChildTemplates] = useState<Template[]>([]);
  const [isChatSection, setIsChatSection] = useState(false);
  const [isLiveChat, setIsLiveChat] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingTemplateId, setLoadingTemplateId] = useState<number | null>(
    null
  );

  const selectedLanguage = useSelector(
    (state: RootState) => state.language.selectedLanguage?.code
  );
  const translations = useSelector(
    (state: RootState) => state.translations?.data
  );

  const theme = useAppSelector((state: RootState) => state.theme.mode);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    SupportServices.allChatbotTemplate().then((res) => {
      setChatBotTemplates(res.data.data);
    });
  }, [selectedLanguage]);

  const token = localStorage.getItem("BscToken");

  const getChildTemplate = (id: number) => {
    setLoadingTemplateId(id);
    SupportServices.childChatbotTemplate(id)
      .then((res) => {
        const children = res.data.data;
        setChildTemplates(
          children.map((child: any) => ({
            id: child.id,
            title: child.title,
            is_parent: child.is_parent,
            title_link: child.title_link,
            link_title: child.link_title,
            children: child.children,
            content: child.content,
          }))
        );

        if (children.length > 0) {
          const botMessages = children.map((child: Template) => {
            const isLink = !!(child.title_link && child.link_title);
            return {
              id: child.id,
              message: isLink
                ? `${child.title} <a href="${child.title_link}" target="_blank" rel="noopener noreferrer">${child.link_title}</a>`
                : child.title,
              isUser: false,
              isParent: child.is_parent,
              hasChildren:
                child.is_parent ||
                (child.children && child.children.length > 0),
              templateId: child.id,
              isLink,
              is_file: 0,
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            } as Message;
          });
          setMessages((prev) => [...prev, ...botMessages]);
        }
      })
      .catch(() => {
        const errorMessage: Message = {
          id: Date.now(),
          message: translations?.error_occured,
          is_file: 0,
          isUser: false,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => [...prev, errorMessage]);
      })
      .finally(() => {
        setLoadingTemplateId(null);
      });
  };

  const showFeedbackQuestion = (botMessages: Message) => {
    const yesNoOptions: Message[] = [
      {
        id: Date.now() + 1,
        message: translations?.yess,
        is_file: 0,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
      {
        id: Date.now() + 2,
        message: translations?.nope,
        is_file: 0,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
      {
        id: Date.now() + 3,
        message: translations?.end_chat,
        is_file: 0,
        isUser: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ];
    setMessages((prev) => [...prev, botMessages, ...yesNoOptions]);
  };

  const handleTemplateSelect = (template: Template) => {
    const userMessage: Message = {
      id: template.id,
      message: template.title,
      is_file: template.is_file,
      isUser: true,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages((prev) => [...prev, userMessage]);
    if (template.is_parent) {
      setChildTemplates([]);
      getChildTemplate(template.id);
    }
  };

  const handleCloseClick = () => {
    setMessages([]);
    setChildTemplates([]);
    setIsChatSection(false);
    setIsLiveChat(false);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleMessageClick = (msg: Message) => {
    if (!msg.isUser && !msg.isContent) {
      if (msg.message === translations?.yess) {
        const feedbackResponse: Message = {
          id: msg.id,
          message: translations?.yes_helped,
          is_file: 0,
          isUser: false,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => [...prev, feedbackResponse]);
        setTimeout(handleCloseClick, 3000);
      } else if (msg.message === translations?.nope) {
        const feedbackResponse: Message = {
          id: msg.id,
          message: translations?.no_not_helped,
          isUser: false,
          is_file: 0,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => [...prev, feedbackResponse]);
        setTimeout(handleCloseClick, 3000);
      } else if (msg.message === translations?.end_chat) {
        handleCloseClick();
      } else {
        const selectedTemplate = childTemplates.find(
          (t) => t.title === msg.message || t.id === msg.templateId
        );
        if (selectedTemplate) {
          handleTemplateSelect(selectedTemplate);
        } else if (msg.templateId) {
          getChildTemplate(msg.templateId);
        }
      }
    }
  };

  return (
    <div style={{ width: "100%" }}>
      {!isLiveSupport && (
        <div className={styles.floatingButtons}>
          <WhatsAppButton />
          <img
            onClick={() => setIsLiveSupport(true)}
            src={MessageIcon}
            alt="Message Icon"
            className={styles.messageIcon}
            width="60"
            height="60"
          />
        </div>
      )}

      {isLiveSupport && (
        <div className={styles.liveSupportWrapper}>
          <div className={styles.liveSupport}>
            <button
              className={styles.closeButton}
              onClick={() => {
                handleCloseClick();
                setIsLiveSupport(false);
              }}
            >
              X
            </button>

            {/* X-dən sonrakı bütün kontent scroll olunan body-dədir */}
            <div className={styles.liveSupportBody}>
              {/* İlk seçimlər */}
              <div className={styles.content}>
                {chatBotTemplates?.map((item, index) => (
                  <div key={index}>
                    <button
                      className={styles.liveBotButton}
                      onClick={() => {
                        handleTemplateSelect(item);
                        setIsChatSection(true);
                        setIsLiveChat(false);
                      }}
                    >
                      {item.title}
                    </button>
                  </div>
                ))}

                <button
                  disabled={isLiveChat}
                  className={styles.liveChatButton}
                  onClick={() => {
                    const check = getCookie("bsc-guest-chat-id");
                    const guest_token = getCookie("bsc-guest-token-chat");
                    if (token) {
                      SupportServices.registerUserForChat().then((res) => {
                        setCookie("bsc-guest-chat-id", res.data.chat_id);
                        setIsLiveChat(true);
                        setMessages([]);
                      });
                    }
                    if (check || guest_token) {
                      setMessages([]);
                      setIsLiveChat(true);
                      setIsChatSection(true);
                      setLoggedIn(false);
                    } else {
                      setIsChatSection(true);
                      setLoggedIn(true);
                      setMessages([]);
                    }
                  }}
                >
                  {translations?.live_chat}
                </button>
              </div>

              {/* login form */}
              {isChatSection && loggedIn && !token && (
                <LiveChatForm
                  setLoggedIn={setLoggedIn}
                  setIsLiveChat={setIsLiveChat}
                />
              )}

              {/* mesaj hissəsi */}
              {isChatSection && (
                <div className={styles.content}>
                  <div className={styles.chat}>
                    <div className={styles.messages}>
                      {messages?.map((msg, idx) => {
                        if (
                          msg.message === translations?.yess ||
                          msg.message === translations?.nope ||
                          msg.message === translations?.end_chat
                        ) {
                          return null;
                        }

                        return msg.templateId === loadingTemplateId ? (
                          <div key={idx} className={styles.message}>
                            ....
                          </div>
                        ) : (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              gap: 10,
                              justifyContent: msg.isUser
                                ? "flex-end"
                                : "flex-start",
                            }}
                          >
                            <div
                              className={`${styles.message} ${msg.isUser
                                  ? styles.message
                                  : msg.isContent
                                    ? styles.contentMessage
                                    : styles.botMessage
                                }`}
                              onClick={() => {
                                if (!msg.isUser && msg.hasChildren) {
                                  handleMessageClick(msg);
                                }
                              }}
                              style={{
                                cursor:
                                  !msg.isUser &&
                                    msg.hasChildren &&
                                    !msg.isContent
                                    ? "pointer"
                                    : "default",
                                fontWeight:
                                  msg.message === translations?.was_it_usefully
                                    ? "bold"
                                    : "normal",
                                fontStyle: msg.isContent ? "italic" : "normal",
                                textAlign: msg.isContent ? "left" : "inherit",
                              }}
                            >
                              {Number(msg.is_file) === 1 ? (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <div>
                                    <div className={styles.previewContent}>
                                      {msg.message.match(
                                        /\.(jpeg|jpg|gif|png|webp)$/
                                      ) ? (
                                        <img
                                          src={msg.message}
                                          alt="preview"
                                          style={{
                                            maxWidth: "80px",
                                            borderRadius: "5px",
                                          }}
                                        />
                                      ) : (
                                        <a
                                          href={msg.message}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={styles.filePreviewLink}
                                        >
                                          {msg.message.split("/").pop()}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : msg.isLink ? (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: msg.message,
                                  }}
                                />
                              ) : (
                                msg.message
                              )}
                            </div>
                            <div ref={messagesEndRef} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {isLiveChat && <LiveChatSection setMessages={setMessages} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveSupportComponent;
